import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Lock, User, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fetchInvitation, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/site/Logo";

export default function AcceptInvite() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { acceptInvite } = useAuth();
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchInvitation(token)
            .then((data) => { setInvite(data); setName(data.name || ""); })
            .catch((err) => setError(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) { setError("Adgangskoden skal være mindst 6 tegn."); return; }
        if (password !== confirm) { setError("Adgangskoderne er ikke ens."); return; }
        setSubmitting(true);
        try {
            const invitedUser = await acceptInvite({ token, name, password });
            const target = (invitedUser?.role || "").toLowerCase() === "medarbejder" ? "/medarbejder" : "/dashboard";
            navigate(target, { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/30 px-4 py-12">
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-6"><Logo height={60} /></div>
                <div data-testid="invite-card" className="bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-200/70 p-7">
                    {invite ? (
                        <>
                            <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium mb-3">
                                <CheckCircle2 className="w-4 h-4" />
                                Invitation modtaget
                            </div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Tilslut {invite.company_name}</h1>
                            <p className="text-sm text-slate-500 mt-1">
                                Rolle: <span className="font-medium text-slate-700">{invite.role}</span>
                                {invite.team ? ` · Team: ${invite.team}` : ""}
                            </p>

                            <form onSubmit={submit} className="mt-6 space-y-4">
                                <div>
                                    <Label htmlFor="iv-name" className="text-slate-700">Navn</Label>
                                    <div className="relative mt-1.5">
                                        <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <Input id="iv-name" required data-testid="invite-name-input" value={name} onChange={(e) => setName(e.target.value)} className="pl-9 h-11" />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-slate-700">Email</Label>
                                    <Input value={invite.email} disabled className="h-11 mt-1.5 bg-slate-50" />
                                </div>
                                <div>
                                    <Label htmlFor="iv-pw" className="text-slate-700">Adgangskode</Label>
                                    <div className="relative mt-1.5">
                                        <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <Input id="iv-pw" type="password" required data-testid="invite-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 h-11" placeholder="Min. 6 tegn" />
                                    </div>
                                </div>
                                <div>
                                    <Label htmlFor="iv-cf" className="text-slate-700">Gentag adgangskode</Label>
                                    <div className="relative mt-1.5">
                                        <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                        <Input id="iv-cf" type="password" required data-testid="invite-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="pl-9 h-11" />
                                    </div>
                                </div>
                                {error && <div data-testid="invite-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
                                <Button type="submit" disabled={submitting} data-testid="invite-submit-btn" className="w-full h-11 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md">
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                    Tilslut og opret konto
                                </Button>
                            </form>
                        </>
                    ) : (
                        <div>
                            <h1 className="text-xl font-semibold text-slate-900">Ugyldig invitation</h1>
                            <p className="text-sm text-slate-500 mt-2">{error || "Linket er udløbet eller allerede brugt."}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
